import FixedBottomContainer from '@/components/FixedBottomContainer'
import Loading from '@/components/Loading'
import FilterSection from '@/components/PCP/FilterSection'
import StaticFilterSection from '@/components/PCP/StaticFilterSection'
import WifButton from '@/components/WifButton'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useGetCollectionFilterFacetsQuery } from '../../shopify/api'

interface FilterModalProps {
  id: string
  collectionHandle: string
  handleClose: () => void
  onRefetch: ({ category }: { category?: string }) => void
}

const FilterModal: React.FC<FilterModalProps> = ({
  id,
  collectionHandle,
  handleClose,
  onRefetch,
}) => {
  const router = useRouter()
  if (!router.isReady) return null
  const { category } = router.query

  const { t } = useTranslation(['pcp'])

  const categoryIdPrefix = 'filter.p.product_type'

  const [selectedCategoryURLEncoded, setSelectedCategoryURLEncoded] = useState(category || '')

  const { loading, data, fetchMore } = useGetCollectionFilterFacetsQuery({
    variables: {
      handle: collectionHandle,
    },
  })

  if (loading) return <Loading />
  if (!data?.collectionByHandle?.products?.filters) return null

  const { title: collectionTitle, products } = data.collectionByHandle
  const { hasNextPage, endCursor } = products.pageInfo

  if (hasNextPage) {
    fetchMore({
      variables: {
        handle: collectionHandle,
        after: endCursor,
      },
    })
  }

  // Product type section
  const { filters } = products
  const categories = filters.find(({ id }) => id === categoryIdPrefix)

  if (!categories) return null

  const handleSelectCategory = (categoryHandle: string) => {
    setSelectedCategoryURLEncoded(categoryHandle)
  }

  const handleApplyFilters = () => {
    const currentBaseUrl = router.asPath
    const newParamsObject = {
      category: router.query.category,
    }

    switch (selectedCategoryURLEncoded) {
      case '':
        delete newParamsObject.category
        break
      default:
        newParamsObject.category = selectedCategoryURLEncoded
        break
    }

    const params = new URLSearchParams(newParamsObject as Record<string, string>).toString()

    let nextUrl = currentBaseUrl.split('?')[0]
    if (params.length > 0) {
      nextUrl += `?${params}`
    }

    handleClose()

    const categoryFilter = {
      category:
        selectedCategoryURLEncoded === ''
          ? undefined
          : decodeURIComponent(selectedCategoryURLEncoded as string),
    }

    onRefetch(categoryFilter)
    router.push(nextUrl)
  }

  return (
    <div data-test={`filter-modal-${id}`}>
      <div className='px-4 py-[32px] h-[calc(100vh-130px)] overflow-y-scroll'>
        <FilterSection
          title={t('filter.categories')}
          items={categories}
          handleSelectItem={handleSelectCategory}
          currentSelectedItem={selectedCategoryURLEncoded as string}
        />
        <StaticFilterSection title={t('filter.collections')} item={collectionTitle} />
      </div>
      <FixedBottomContainer>
        <WifButton
          id='apply-filter-button'
          handleClick={handleApplyFilters}
          text={t('filter.show_products')}
          type='primary'
          data-test={`filter-modal-${id}-apply-filter-button`}
        />
      </FixedBottomContainer>
    </div>
  )
}

export default FilterModal
